import React, { KeyboardEvent, ReactNode } from "react";
import { connect } from "react-redux";

import { defineMessage, useIntl } from "react-intl";

import SavedAddressType from "@mapmycustomers/shared/enum/SavedAddressType";

import { RootState } from "@app/store/rootReducer";
import { getReservedSavedAddress, getSavedAddressLimit } from "@app/store/savedLocations";
import { deleteSavedAddress, updateSavedAddress } from "@app/store/savedLocations/actions";
import { savedAddressErrorMessages } from "@app/store/savedLocations/messages";
import {
  getSavedLocationNamingCard,
  hideSavedLocationNamingCard,
} from "@app/store/savedLocationsUi";
import { SAVED_ADDRESS_LIMIT_CODE } from "@app/util/savedLocations/savedAddressErrors";

import DismissButton from "./DismissButton";
import NamingForm from "./NamingForm";
import styles from "./SavedLocationNamingCard.module.scss";

const blockedMessage = defineMessage({
  id: "savedLocations.namingCard.blocked",
  defaultMessage:
    "<b>Not saved.</b> You are holding {limit, plural, one {# saved location} other {# saved locations}}, which is the limit. Home and Work count toward it. Delete one, then save this place again.",
  description:
    "Card shown when a place couldn't be saved because the rep is at the saved locations limit",
});

const bold = (text: ReactNode) => <b>{text}</b>;

// Universal search listens for keys on document.body, so typing in the card mustn't reach it
const stopPropagation = (event: KeyboardEvent) => event.stopPropagation();

const SavedLocationNamingCard = ({
  home,
  limit,
  namingCard,
  onDelete,
  onHide,
  onUpdate,
  work,
}: ReturnType<typeof mapStateToProps> & typeof mapDispatchToProps) => {
  const intl = useIntl();

  if (!namingCard) {
    return null;
  }

  return (
    <div
      className={styles.container}
      onKeyDown={stopPropagation}
      onKeyUp={stopPropagation}
      role="status"
    >
      {namingCard.kind === "blocked" ? (
        <>
          <div className={styles.message}>
            {limit === undefined
              ? intl.formatMessage(savedAddressErrorMessages[SAVED_ADDRESS_LIMIT_CODE])
              : intl.formatMessage(blockedMessage, { b: bold, limit })}
          </div>
          <DismissButton onClick={onHide} />
        </>
      ) : (
        <NamingForm
          home={home}
          key={namingCard.savedAddress.id}
          onDelete={onDelete}
          onHide={onHide}
          onUpdate={onUpdate}
          savedAddress={namingCard.savedAddress}
          work={work}
        />
      )}
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  home: getReservedSavedAddress(state, SavedAddressType.HOME),
  limit: getSavedAddressLimit(state),
  namingCard: getSavedLocationNamingCard(state),
  work: getReservedSavedAddress(state, SavedAddressType.WORK),
});

const mapDispatchToProps = {
  onDelete: deleteSavedAddress.request,
  onHide: hideSavedLocationNamingCard,
  onUpdate: updateSavedAddress.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(SavedLocationNamingCard);
